import { MultiplayerClient, type MultiplayerOptions } from '@evoke/multiplayer-client';
export type * from './models';

export interface AnalyticsContext { platform: 'web' | 'ios' | 'android'; source: 'landing' | 'invite' | 'create' | 'resume' | 'mobile' }
export interface Credentials { persistentId: string; reconnectToken: string }
export interface ClientOptions extends Pick<MultiplayerOptions, 'serverUrl' | 'log' | 'createSocket'> {
  credentials: () => Credentials;
  analytics?: () => AnalyticsContext;
}

interface EvokeCommands {
  'create-room': [payload: Credentials & { nickname: string; analytics?: AnalyticsContext }];
  'join-room': [payload: Credentials & { roomCode: string; nickname: string; analytics?: AnalyticsContext }];
  'start-game': [];
  'submit-story': [payload: { story: string; cardId: number }];
  'submit-card': [payload: { cardId: number }];
  'submit-vote': [payload: { cardId: number }];
  'start-next-round': [];
  'change-avatar': [payload: { avatarId: string }];
  'change-nickname': [payload: { newNickname: string }];
  'skip-disconnected-players': [];
  'leave-room': [];
  'return-to-lobby': [];
  'update-room-settings': [settings: { mode: 'score-target' | 'best-of-rounds'; targetScore: number; targetRounds: number }];
}

/** Evoke's wire protocol and credentials over the reusable connection lifecycle. */
export class SocketService extends MultiplayerClient<EvokeCommands> {
  constructor(private readonly clientOptions: ClientOptions) {
    super({
      serverUrl: clientOptions.serverUrl,
      createSocket: clientOptions.createSocket,
      log: clientOptions.log,
      membership: { joined: ['room-created', 'room-joined'], left: ['left-room', 'session-replaced'] },
      heartbeat: { ping: 'heartbeat-ping', pong: 'heartbeat-pong', intervalMs: 10000, timeoutMs: 5000, maxMissed: 3 },
    });
  }

  createRoom(nickname: string) {
    if (!this.getSocket()?.connected) return;
    this.send('create-room', [{ nickname, ...this.clientOptions.credentials(), ...(this.clientOptions.analytics ? { analytics: this.clientOptions.analytics() } : {}) }], false);
  }

  joinRoom(roomCode: string, nickname: string) {
    if (!this.getSocket()?.connected) return;
    this.send('join-room', [{ roomCode, nickname, ...this.clientOptions.credentials(), ...(this.clientOptions.analytics ? { analytics: this.clientOptions.analytics() } : {}) }], false);
  }

  startGame() {
    this.send('start-game', []);
  }

  submitStory(story: string, cardId: number) {
    this.send('submit-story', [{ story, cardId }]);
  }

  submitCard(cardId: number) {
    this.send('submit-card', [{ cardId }]);
  }

  submitVote(cardId: number) {
    this.send('submit-vote', [{ cardId }]);
  }

  startNextRound() {
    this.send('start-next-round', []);
  }

  changeAvatar(avatarId: string): Promise<boolean> {
    return this.sendAndWait('change-avatar', [{ avatarId }], { successEvent: 'avatar-changed', errorEvents: ['game-error'] });
  }

  changeNickname(newNickname: string) {
    this.send('change-nickname', [{ newNickname }]);
  }

  skipDisconnectedPlayers() {
    this.send('skip-disconnected-players', []);
  }

  leaveRoom(): Promise<boolean> {
    return this.sendAndWait('leave-room', [], {
      successEvent: 'left-room', errorEvents: ['game-error'], requireMembership: false,
    });
  }

  returnToLobby() {
    this.send('return-to-lobby', []);
  }

  updateRoomSettings(settings: { mode: 'score-target' | 'best-of-rounds'; targetScore: number; targetRounds: number }) {
    this.send('update-room-settings', [settings]);
  }
}

